import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, CssBaseline, useMediaQuery } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../redux/store";
import Navbar, { FLOATING_NAVBAR_HEIGHTS } from "../../component/organisms/layoutComponent/Navbar";
import Sidebar from "../../component/organisms/layoutComponent/Sidebar";
import SubHeader from "../../component/organisms/layoutComponent/SubHeader";
import RetailerAdvertisementPopup from "../../component/molecules/RetailerAdvertisementPopup";
// import FloatingChat from "../../component/atoms/FloatingChat";

// Import icons
import companyIcon from '../../assets/bag.svg';
import accountIcon from '../../assets/bank.svg';
import phoneIcon from '../../assets/phoneCall.svg';
import locationIcon from '../../assets/location.svg';
import routeIcon from '../../assets/route.svg';
import stopIcon from '../../assets/flag.svg';
import UserIcon from '../../assets/icons/user_1.svg';
// Figma "Distributor Details" popup icons — stroke colors are baked into the
// SVGs, so each carries its own matching box tint below.
import distributorCompanyIcon from '../../assets/distributorCompany.svg';
import distributorPhoneIcon from '../../assets/distributorPhone.svg';
import distributorAddressIcon from '../../assets/distributorAddress.svg';

// Icon-box tints from Figma, matching each icon's stroke color
const COMPANY_TINT = '#EFF6FF';  // blue (#155DFC icon)
const PHONE_TINT = '#F0FDF4';    // green (#0CAB47 icon)
const ADDRESS_TINT = '#FAF5FF';  // purple (#9810FA icon)

const FULL_DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 65;
const NAVBAR_HEIGHT = 65;
const DETAILS_BAR_HEIGHT = 53;
// Gap between the floating sidebar / navbar cards and the screen edges
// (Figma spec: 16px).
const SIDEBAR_MARGIN = 16;
// Expanded desktop sidebar auto-collapses after this long without any mouse
// movement over it.
const SIDEBAR_AUTO_COLLAPSE_MS = 4000;
// Hovering the collapsed sidebar only expands it after the pointer has stayed
// on it this long; leaving earlier cancels the expand. A click expands instantly.
const SIDEBAR_HOVER_EXPAND_MS = 1500;
// Shared page frame: every page sits in a centred column of this max width (the
// same 1600px the DashboardV1 frames use), so Orders, Call List, Products…
// all line up. Pages that carry their own sub-sidebar (vertical rail) need the
// room and stay full width.
const CONTENT_MAX_WIDTH = 1600;
const FULL_WIDTH_PAGE =
  /^(?:\/(?:admin|sales|retailer))?\/(?:settings|promo|reports-analytics|setup-v1|e-pick-settings|receivable-settings|driver-management)(?:\/|$)/;

// Define the header data for different tabs

const AdminLayout: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:1199px)");
  // ≤820px (the DashboardV1 tabs' mobile breakpoint) pages stack vertically and
  // expect normal document scroll — an inner viewport-height scrollport there
  // leaves the scrollbar floating mid-screen instead of at the screen edge.
  const isPhone = useMediaQuery("(max-width:820px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  // Details bar starts closed: it now opens as an overlay popup below the
  // navbar (it no longer pushes the page content down).
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const { pathname } = useLocation();
  const isFullWidthPage = FULL_WIDTH_PAGE.test(pathname);

  const layout = useSelector((state: RootState) => state.layout.layout);
  const isVertical = layout === "vertical";
  const auth = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const headerData = {
    0: [ // Store Details
      { icon: companyIcon, label: t("storeHeader.companysName"), value: auth.storeDetail?.C_Name || auth.storeDetail?.C_CoName || '-' },
      { icon: accountIcon, label: t("storeHeader.accountNumber"), value: auth.storeDetail?.C_Number || '-' },
      { icon: locationIcon, label: t("storeHeader.businessAddress"), value: `${auth.storeDetail?.C_Address || ''}, ${auth.storeDetail?.C_City || ''}, ${auth.storeDetail?.C_State || ''}, ${auth.storeDetail?.C_Zip || ''}` },
      { icon: phoneIcon, label: t("storeHeader.phoneNumber"), value: auth.storeDetail?.C_Phone || '-' },
      { icon: routeIcon, label: t("storeHeader.routes"), value: String(auth.storeDetail?.Routes?.[0]?.Route_Number || '-') },
      { icon: stopIcon, label: t("storeHeader.stops"), value: String(auth.storeDetail?.Routes?.[0]?.Stop_Number || '-') },
      { icon: UserIcon, label: t("storeHeader.salesRep"), value: auth.storeDetail?.salesRep?.S_Desc || '-' },

    ],
    1: [ // Distributor Details
      { icon: distributorCompanyIcon, tint: COMPANY_TINT, label: t("storeHeader.distributorsCompany"), value: auth.wareHouseDetail?.[0]?.D_Name || '-' },
      { icon: distributorAddressIcon, tint: ADDRESS_TINT, label: t("storeHeader.businessAddress"), value: `${auth.wareHouseDetail?.[0]?.D_Addr1 || ''}, ${auth.wareHouseDetail?.[0]?.D_City || ''}, ${auth.wareHouseDetail?.[0]?.D_State || ''}` },
      { icon: distributorPhoneIcon, tint: PHONE_TINT, label: t("storeHeader.contactNumber"), value: auth.wareHouseDetail?.[0]?.D_Phone || '-' },
    ],
  };
  const staticHeaderDataForDistributor = {
    0: [ // Distributor Details
      { icon: distributorCompanyIcon, tint: COMPANY_TINT, label: t("storeHeader.distributorsCompany"), value: auth.wareHouseDetail?.[0]?.D_Name || '-' },
      // { icon: stopIcon, label: t("storeHeader.salesRep"), value: auth.storeDetail?.salesRep?.S_Desc || '-' },
      // { icon: routeIcon, label: t("storeHeader.routes"), value: String(auth.storeDetail?.Routes?.[0]?.Route_Number || '-') },
      // { icon: stopIcon, label: t("storeHeader.stops"), value: String(auth.storeDetail?.Routes?.[0]?.Stop_Number || '-') },
      { icon: distributorPhoneIcon, tint: PHONE_TINT, label: t("storeHeader.phoneNumber"), value: auth.wareHouseDetail?.[0]?.D_Phone || '-' },
      { icon: distributorAddressIcon, tint: ADDRESS_TINT, label: t("storeHeader.businessAddress"), value: `${auth.wareHouseDetail?.[0]?.D_Addr1 || ''}, ${auth.wareHouseDetail?.[0]?.D_City || ''}, ${auth.wareHouseDetail?.[0]?.D_State || ''}` },
    ],
  };
  const staticHeaderDataForSales = {
    0: [ // Store Details
      { icon: companyIcon, label: t("storeHeader.companysName"), value: auth.storeDetail?.C_Name || auth.storeDetail?.C_CoName || '-' },
      { icon: accountIcon, label: t("storeHeader.accountNumber"), value: auth.storeDetail?.C_Number || '-' },
      { icon: locationIcon, label: t("storeHeader.businessAddress"), value: `${auth.storeDetail?.C_Address || ''}, ${auth.storeDetail?.C_City || ''}, ${auth.storeDetail?.C_State || ''}, ${auth.storeDetail?.C_Zip || ''}` },
      { icon: phoneIcon, label: t("storeHeader.phoneNumber"), value: auth.storeDetail?.C_Phone || '-' },
      { icon: routeIcon, label: t("storeHeader.routes"), value: String(auth.storeDetail?.Routes?.[0]?.Route_Number || '-') },
      { icon: stopIcon, label: t("storeHeader.stops"), value: String(auth.storeDetail?.Routes?.[0]?.Stop_Number || '-') },
      { icon: UserIcon, label: t("storeHeader.salesRep"), value: auth.storeDetail?.salesRep?.S_Desc || '-' },

    ],
    1: [ // Wholesale Details
      { icon: distributorCompanyIcon, tint: COMPANY_TINT, label: t("storeHeader.distributorsCompany"), value: auth.wareHouseDetail?.[0]?.D_Name || '-' },
      { icon: distributorAddressIcon, tint: ADDRESS_TINT, label: t("storeHeader.businessAddress"), value: `${auth.wareHouseDetail?.[0]?.D_Addr1 || ''}, ${auth.wareHouseDetail?.[0]?.D_City || ''}, ${auth.wareHouseDetail?.[0]?.D_State || ''}` },
      { icon: distributorPhoneIcon, tint: PHONE_TINT, label: t("storeHeader.contactNumber"), value: auth.wareHouseDetail?.[0]?.D_Phone || '-' },
    ],
  };
  
  // Get the header items based on the selected tab
  const currentHeaderItems = selectedTab !== null ? auth?.role === "distributor" ? staticHeaderDataForDistributor[selectedTab as keyof typeof staticHeaderDataForDistributor] || [] : auth?.role === "sales" ? staticHeaderDataForSales[selectedTab as keyof typeof staticHeaderDataForSales] || [] : headerData[selectedTab as keyof typeof headerData] || [] : [];

  // Auto-collapse: while the desktop sidebar is expanded, count down
  // SIDEBAR_AUTO_COLLAPSE_MS (4s) and collapse it; any mouse movement over
  // the sidebar restarts the countdown.
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const restartAutoCollapseTimer = useCallback(() => {
    if (autoCollapseTimerRef.current) clearTimeout(autoCollapseTimerRef.current);
    autoCollapseTimerRef.current = setTimeout(
      () => setCollapsed(true),
      SIDEBAR_AUTO_COLLAPSE_MS
    );
  }, []);

  useEffect(() => {
    // Only the expanded permanent (desktop, vertical-layout) drawer auto-collapses;
    // the mobile temporary drawer and horizontal layout are untouched.
    if (isMobile || collapsed || !isVertical) return;
    restartAutoCollapseTimer();
    return () => {
      if (autoCollapseTimerRef.current) clearTimeout(autoCollapseTimerRef.current);
    };
  }, [isMobile, collapsed, isVertical, restartAutoCollapseTimer]);

  // Hover-expand with intent delay: the collapsed sidebar only expands after
  // the pointer has stayed on it for SIDEBAR_HOVER_EXPAND_MS; leaving earlier
  // cancels it, and a click expands instantly. Closing is left to the normal
  // auto-collapse above: 4s without mouse movement over the sidebar.
  const hoverExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHoverExpand = useCallback(() => {
    if (hoverExpandTimerRef.current) {
      clearTimeout(hoverExpandTimerRef.current);
      hoverExpandTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelHoverExpand, [cancelHoverExpand]);

  const handleSidebarMouseEnter = () => {
    if (!isMobile && collapsed && !hoverExpandTimerRef.current) {
      hoverExpandTimerRef.current = setTimeout(() => {
        hoverExpandTimerRef.current = null;
        setCollapsed(false);
      }, SIDEBAR_HOVER_EXPAND_MS);
    }
  };

  const handleSidebarMouseLeave = () => {
    cancelHoverExpand();
  };

  // Any click on the collapsed sidebar expands it immediately (no hover wait).
  const handleSidebarClick = () => {
    if (!isMobile && collapsed) {
      cancelHoverExpand();
      setCollapsed(false);
    }
  };

  const handleDrawerToggle = () => {
    cancelHoverExpand();
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleTabChange = (tab: number) => {
    if (tab === selectedTab && isDetailsOpen) {
      // Clicking the open tab again collapses the details popup
      setIsDetailsOpen(false);
      setSelectedTab(null);
      return;
    }
    // If SubHeader was closed, open it
    setIsDetailsOpen(true);
    setSelectedTab(tab);
  };

  const handleSubHeaderClose = () => {
    setIsDetailsOpen(false);
    setSelectedTab(null);
  };

  // Hover behavior for the details popup (vertical layout): hovering a navbar
  // tab opens it, leaving both the tab and the popup closes it after a short
  // grace delay (so the pointer can travel from the tab into the popup).
  const detailsCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDetailsClose = useCallback(() => {
    if (detailsCloseTimerRef.current) {
      clearTimeout(detailsCloseTimerRef.current);
      detailsCloseTimerRef.current = null;
    }
  }, []);

  const handleTabHover = useCallback(
    (tab: number) => {
      cancelDetailsClose();
      setIsDetailsOpen(true);
      setSelectedTab(tab);
    },
    [cancelDetailsClose]
  );

  const handleDetailsHoverEnd = useCallback(() => {
    cancelDetailsClose();
    detailsCloseTimerRef.current = setTimeout(() => {
      detailsCloseTimerRef.current = null;
      setIsDetailsOpen(false);
      setSelectedTab(null);
    }, 250);
  }, [cancelDetailsClose]);

  useEffect(() => cancelDetailsClose, [cancelDetailsClose]);

  // Automatically open the drawer on desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
      setCollapsed(false);
    }
  }, [isMobile]);

  const getContentHeight = () => {
    const baseHeight = `calc(100vh - ${NAVBAR_HEIGHT}px`;
    if (isDetailsOpen && selectedTab !== null) {
      return `${baseHeight} - ${DETAILS_BAR_HEIGHT}px)`;
    }
    return `${baseHeight})`;
  };

  // Role gate for the store/distributor details bar (unchanged logic).
  const canShowDetails =
    auth?.role === "sales"
      ? auth?.module?.some(
          (moduleItem: any) =>
            moduleItem.module === "Dashboard" && moduleItem?.view === true
        )
      : true;

  // In the vertical layout the details bar overlays the content as a popup, so
  // the content height only accounts for the navbar area: the floating navbar
  // card (responsive height, Figma max 68px) + its 16px top gap. Kept in sync
  // with Navbar via the shared FLOATING_NAVBAR_HEIGHTS breakpoints.
  const verticalContentHeight = {
    xs: `calc(100vh - ${FLOATING_NAVBAR_HEIGHTS.xs + SIDEBAR_MARGIN}px)`,
    md: `calc(100vh - ${FLOATING_NAVBAR_HEIGHTS.md + SIDEBAR_MARGIN}px)`,
    lg: `calc(100vh - ${FLOATING_NAVBAR_HEIGHTS.lg + SIDEBAR_MARGIN}px)`,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isPhone ? "auto" : "100vh",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <CssBaseline />

      {isVertical ? (
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Sidebar
            variant="vertical"
            open={mobileOpen || !isMobile}
            onClose={handleDrawerToggle}
            onToggle={handleDrawerToggle}
            collapsed={!mobileOpen && !isMobile && collapsed}
            onMouseMove={
              !isMobile && !collapsed ? restartAutoCollapseTimer : undefined
            }
            onMouseEnter={!isMobile ? handleSidebarMouseEnter : undefined}
            onMouseLeave={!isMobile ? handleSidebarMouseLeave : undefined}
            onClick={!isMobile ? handleSidebarClick : undefined}
          />

          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              ml: isMobile
                ? 0
                : `${
                    (collapsed ? COLLAPSED_DRAWER_WIDTH : FULL_DRAWER_WIDTH) +
                    SIDEBAR_MARGIN
                  }px`,
              // same curve as the drawer's width transition so the page and
              // sidebar move together instead of drifting apart mid-animation
              transition: "margin-left 0.3s ease-in-out",
            }}
          >
            {/* Floating navbar card. position:relative anchors the details
                popup right below it, overlaying the page content. On phones the
                document scrolls (not the main box), so the wrapper turns sticky
                — the navbar stays pinned while the page scrolls beneath it. */}
            <Box
              sx={{
                position: isPhone ? "sticky" : "relative",
                top: isPhone ? 0 : "auto",
                // mask the page content sliding under the sticky bar
                bgcolor: isPhone ? "background.default" : "transparent",
                px: 2,
                pt: 2,
                pb: isPhone ? 1 : 0,
                zIndex: (theme) => theme.zIndex.appBar,
              }}
            >
              <Navbar
                onDrawerToggle={handleDrawerToggle}
                onTabChange={handleTabChange}
                onTabHover={handleTabHover}
                onTabHoverEnd={handleDetailsHoverEnd}
                selectedTab={selectedTab ?? -1}
                isDrawerOpen={isMobile ? mobileOpen : collapsed}
              />
              {canShowDetails && selectedTab !== null && (
                <Box
                  onMouseEnter={cancelDetailsClose}
                  onMouseLeave={handleDetailsHoverEnd}
                  sx={{
                    position: "absolute",
                    // Flush against the navbar card: 1px overlap so the two
                    // borders merge into a single seam (no gap, like Figma)
                    top: "calc(100% - 1px)",
                    left: 16,
                    right: 16,
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                  }}
                >
                  <SubHeader
                    isOpen={isDetailsOpen}
                    onClose={handleSubHeaderClose}
                    items={currentHeaderItems}
                    variant="popup"
                  />
                </Box>
              )}
            </Box>

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                // 16px side padding so the page content aligns exactly with
                // the floating navbar card edges (navWrap px: 2).
                px: 2,
                py: 2,
                overflowY: isPhone ? "visible" : "auto",
                // Definite height (not just maxHeight) so a page can bound itself to
                // the viewport and manage its own inner scroll (e.g. Create PO).
                // On phones the document scrolls instead, so the height stays free.
                height: isPhone ? "auto" : verticalContentHeight,
                maxHeight: isPhone ? "none" : verticalContentHeight,
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  maxWidth: isFullWidthPage ? "none" : CONTENT_MAX_WIDTH,
                  mx: "auto",
                }}
              >
                <Outlet />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Navbar
            onDrawerToggle={handleDrawerToggle}
            onTabChange={handleTabChange}
            selectedTab={selectedTab ?? -1}
            isDrawerOpen={isMobile ? mobileOpen : collapsed}
          />
          {canShowDetails && selectedTab !== null && (
            <SubHeader
              isOpen={isDetailsOpen}
              onClose={handleSubHeaderClose}
              items={currentHeaderItems}
            />
          )}
          <Sidebar variant="horizontal" />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 2,
              overflowY: isPhone ? "visible" : "auto",
              maxHeight: isPhone ? "none" : getContentHeight(),
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: CONTENT_MAX_WIDTH }}>
              <Outlet />
            </Box>
          </Box>
        </>
      )}
      
      {/* Daily advertisement pop-up - retailers only */}
      {auth?.role === "retailer" && <RetailerAdvertisementPopup />}

      {/* Floating Chat - Only visible for distributors */}
      {/* <FloatingChat /> */}
    </Box>
  );
};

export default AdminLayout;
